import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import type { PricingTierDetailDto } from "@/types/pricing-tier";
import {
  useCreatePricingTier,
  useUpdatePricingTier,
} from "@/hooks/usePricingTiers";
import {
  formatCurrency,
  fromMinorUnits,
  getDecimalPlaces,
  toMinorUnits,
  type CurrencyCode,
} from "@/lib/currency";

// ============================================================================
// Validation Schema
// ============================================================================

const formSchema = z
  .object({
    tierName: z.string().optional(),
    minQuantity: z.coerce
      .number()
      .min(0, "Min quantity must be >= 0")
      .int("Must be a whole number"),
    maxQuantity: z.coerce.number().optional().nullable(),
    hasNoMaxQuantity: z.boolean().default(false),
    pricePerUnit: z.coerce.number().min(0, "Price must be >= 0"),
    flatFee: z.coerce
      .number()
      .min(0, "Flat fee must be >= 0")
      .optional()
      .nullable(),
    tierOrder: z.coerce.number().min(1).optional().nullable(),
    metadata: z.string().optional(),
  })
  .refine(
    (data) => {
      // If hasNoMaxQuantity is true, maxQuantity should be null
      if (data.hasNoMaxQuantity) {
        return true;
      }
      // If hasNoMaxQuantity is false and maxQuantity is provided, validate
      if (data.maxQuantity !== null && data.maxQuantity !== undefined) {
        return data.maxQuantity > data.minQuantity;
      }
      return true;
    },
    {
      message: "Max quantity must be greater than min quantity",
      path: ["maxQuantity"],
    },
  )
  .refine(
    (data) => {
      // If metadata is provided, it must be valid JSON
      if (data.metadata && data.metadata.trim() !== "") {
        try {
          JSON.parse(data.metadata);
          return true;
        } catch {
          return false;
        }
      }
      return true;
    },
    {
      message: "Metadata must be valid JSON",
      path: ["metadata"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

// ============================================================================
// Component Props
// ============================================================================

interface PricingTierFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  componentPriceId: string;
  currency: string;
  tier?: PricingTierDetailDto;
  mode: "create" | "edit";
}

// ============================================================================
// Component
// ============================================================================

export function PricingTierForm({
  open,
  onOpenChange,
  componentPriceId,
  currency,
  tier,
  mode,
}: PricingTierFormProps) {
  const createMutation = useCreatePricingTier();
  const updateMutation = useUpdatePricingTier();
  // One step = one minor unit of this currency. A fixed "0.01" would reject a valid KWD price
  // (3 decimals) outright and invite meaningless fractions of a VND (0 decimals).
  const amountStep = 1 / Math.pow(10, getDecimalPlaces(currency));
  const amountPlaceholder = (0).toFixed(getDecimalPlaces(currency));
  const [previewPrice, setPreviewPrice] = useState<string>("");
  const [previewFlatFee, setPreviewFlatFee] = useState<string>("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: tier
      ? {
          tierName: tier.tierName || "",
          minQuantity: tier.minQuantity,
          maxQuantity: tier.maxQuantity ?? null,
          hasNoMaxQuantity:
            tier.maxQuantity === null || tier.maxQuantity === undefined,
          pricePerUnit: fromMinorUnits(
            tier.pricePerUnit,
            currency as CurrencyCode,
          ),
          flatFee: tier.flatFee
            ? fromMinorUnits(tier.flatFee, currency as CurrencyCode)
            : null,
          tierOrder: tier.tierOrder,
          metadata: tier.metadata || "",
        }
      : {
          tierName: "",
          minQuantity: 0,
          maxQuantity: null,
          hasNoMaxQuantity: false,
          pricePerUnit: 0,
          flatFee: null,
          tierOrder: null,
          metadata: "",
        },
  });

  // Watch price and flat fee fields for preview
  const watchPrice = form.watch("pricePerUnit");
  const watchFlatFee = form.watch("flatFee");
  const watchHasNoMax = form.watch("hasNoMaxQuantity");

  useEffect(() => {
    if (watchPrice !== undefined && watchPrice !== null) {
      setPreviewPrice(formatCurrency(watchPrice, currency));
    }
  }, [watchPrice, currency]);

  useEffect(() => {
    if (
      watchFlatFee !== undefined &&
      watchFlatFee !== null &&
      watchFlatFee > 0
    ) {
      setPreviewFlatFee(formatCurrency(watchFlatFee, currency));
    } else {
      setPreviewFlatFee("");
    }
  }, [watchFlatFee, currency]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  // Clear maxQuantity when hasNoMaxQuantity is checked
  useEffect(() => {
    if (watchHasNoMax) {
      form.setValue("maxQuantity", null);
    }
  }, [watchHasNoMax, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      const payload = {
        tierName: data.tierName || undefined,
        minQuantity: data.minQuantity,
        maxQuantity: data.hasNoMaxQuantity
          ? undefined
          : (data.maxQuantity ?? undefined),
        // The form holds display amounts; the API stores the currency's smallest unit (see
        // PricingCalculationService, which feeds tier.PricePerUnit straight into unitPriceCents).
        // Must mirror the fromMinorUnits() used to load the form — a hardcoded ×100 would multiply
        // a VND tier by 100 on every save, and divide a KWD one by 10.
        pricePerUnit: toMinorUnits(data.pricePerUnit, currency),
        flatFee: data.flatFee
          ? toMinorUnits(data.flatFee, currency)
          : undefined,
        tierOrder: data.tierOrder ?? undefined,
        metadata: data.metadata || undefined,
      };

      if (mode === "create") {
        await createMutation.mutateAsync({
          componentPriceId,
          data: payload,
        });
      } else if (tier) {
        await updateMutation.mutateAsync({
          id: tier.id,
          data: payload,
        });
      }
      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handling is done in the mutation hooks via toast
      console.error("Form submission error:", error);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: form.formState.isDirty,
    isSubmitting,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Create Pricing Tier" : "Edit Pricing Tier"}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={DIALOG_FORM_CLASSES}
            >
              <DialogBody className="space-y-6">
                <DialogDescription>
                  {mode === "create"
                    ? "Create a new volume-based pricing tier. Prices are in smallest currency units (cents)."
                    : "Update the pricing tier details."}
                </DialogDescription>
                {/* Info Alert */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Pricing tiers allow volume discounts. Enter prices in base
                    currency (e.g., dollars, not cents). They will be stored in
                    the smallest unit automatically.
                  </AlertDescription>
                </Alert>

                {/* Tier Name Field */}
                <FormField
                  control={form.control}
                  name="tierName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier Name (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='e.g., "Small Volume", "Enterprise", "Bulk Discount"'
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        A descriptive name for this tier (optional)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Quantity Range Row */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="minQuantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Min Quantity *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="0"
                            {...field}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormDescription>
                          Minimum quantity (inclusive)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="maxQuantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Quantity</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="1"
                            placeholder="Optional"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                            disabled={
                              isSubmitting || form.watch("hasNoMaxQuantity")
                            }
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum quantity (inclusive)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* No Max Quantity Checkbox */}
                <FormField
                  control={form.control}
                  name="hasNoMaxQuantity"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>No Maximum Quantity</FormLabel>
                        <FormDescription>
                          This tier applies to all quantities above the minimum
                          (e.g., "100+ units")
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {/* Price and Flat Fee Row */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="pricePerUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price Per Unit *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step={amountStep}
                            min="0"
                            placeholder={amountPlaceholder}
                            {...field}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormDescription>
                          {previewPrice
                            ? `Preview: ${previewPrice}`
                            : "Price for one unit"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="flatFee"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Flat Fee (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step={amountStep}
                            min="0"
                            placeholder={amountPlaceholder}
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value);
                              field.onChange(value);
                            }}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormDescription>
                          {previewFlatFee
                            ? `Preview: ${previewFlatFee}`
                            : "Optional one-time fee per tier"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Tier Order Field */}
                <FormField
                  control={form.control}
                  name="tierOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier Order (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Auto-assigned if left empty"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            field.onChange(value);
                          }}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        Order in which tiers are displayed (1, 2, 3, etc.).
                        Auto-assigned if not provided.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Metadata Field (Optional) */}
                <FormField
                  control={form.control}
                  name="metadata"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Metadata (JSON)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder='{"key": "value"}'
                          className="resize-none font-mono text-sm"
                          rows={3}
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        Optional JSON metadata for custom properties
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Form Actions */}
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guard.onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? mode === "create"
                      ? "Creating..."
                      : "Updating..."
                    : mode === "create"
                      ? "Create Tier"
                      : "Update Tier"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
